import { App } from "@teamworksdev/react";
import { getPersonName } from "~/config";
import type { DocumentData, DocumentScope, DocumentType } from "../documents";
import type {
  MatchDocument,
  PartialMatch,
  PartialMatchDetail,
  PartialMatchScopeData,
  Person,
  ProfileDocument,
} from "./types";

export function makePartialMatch(json: any): PartialMatch {
  return {
    _original: json,
    app: json.app,
    data: json.data,
    birthdate: json.new_birthdate,
    email: json.new_email,
    familyName: json.new_family_name,
    givenName: json.new_given_name,
    orgId: json.org_id,
    matchDate: json.match_date,
    phone: json.new_phone,
    preferredName: json.new_preferred_name,
    uniqueId: json.new_unique_id,
    partialMatchId: json.partial_match_id,
    name: getPersonName({
      givenName: json.new_given_name,
      preferredName: json.new_preferred_name,
      familyName: json.new_family_name,
    } as Person),
  };
}

function extractScopeData(scopes: DocumentScope<PartialMatchScopeData, typeof DocumentType.profile>) {
  const result: Partial<Record<App, PartialMatchScopeData>> = {};
  if (scopes == null) return result;
  Object.entries(scopes).forEach(([k, v]) => {
    result[k.split(".")[0] as App] = v;
  });
  return result;
}

export function makePartialMatchDetail(json: any): PartialMatchDetail {
  let matchedDocuments: MatchDocument[] = [];
  const partialMatch = makePartialMatch(json) as PartialMatchDetail;

  const scopes = extractScopeData(
    json.data.scopes as DocumentScope<PartialMatchScopeData, typeof DocumentType.profile>,
  );

  Object.values<ProfileDocument>(json.matched_documents).forEach((value) => {
    const allAppEntries = Object.entries(value.data) as [
      App,
      DocumentData<PartialMatchScopeData, typeof DocumentType.profile>,
    ][];

    const additionalFields: Partial<MatchDocument> = {};
    const appScopeData: Partial<Record<App, PartialMatchScopeData>> = {};
    allAppEntries.forEach(([app, docData]) => {
      const _matchScopeData = Object.values(docData.scopes)[0];
      // For each app entry, there is only one scope, which is <app_slug>.profile,
      // so we will extract it here to make the nesting easier to work with. This
      // scope will only have one object, so we should be able to reliably just
      // get the value in the first position to be our scopeData
      if (app === App.axle) {
        additionalFields.clientUniqueId = _matchScopeData.clientUniqueId;
      }
      appScopeData[app] = {
        ..._matchScopeData,
        status: docData.status,
      };
    });

    const document: MatchDocument = {
      ...additionalFields,
      apps: Object.keys(appScopeData) as App[],
      documentId: value.id,
      scopes: appScopeData,
    };

    matchedDocuments = [...matchedDocuments, document];
  });

  return {
    ...partialMatch,
    scopes,
    matchedDocuments,
  };
}

export function makePartialMatches(json: any): PartialMatch[] {
  return json?.map(makePartialMatch) ?? [];
}
